import type { FormEvent } from 'react';
import { memo, useState } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import TelegrafLogo from '../cx/common/TelegrafLogo';
import Button from '../ui/Button';
import InputText from '../ui/InputText';

import styles from './AuthEmail.module.scss';

type StateProps = Pick<GlobalState['auth'], 'isLoading' | 'errorKey'>;

const AuthName = ({ isLoading, errorKey }: StateProps) => {
  const { signUp, clearAuthErrorKey } = getActions();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const lang = useLang();

  const handleFirstNameChange = useLastCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorKey) clearAuthErrorKey();
    setFirstName(e.target.value);
  });

  const handleLastNameChange = useLastCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  });

  const handleSubmit = useLastCallback((e: FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || isLoading) return;

    signUp({ firstName: firstName.trim(), lastName: lastName.trim() });
  });

  return (
    <div className={styles.root}>
      <TelegrafLogo className={styles.logo} withWordmark />
      <h1 className={styles.title}>{lang('CxAuthNameTitle')}</h1>
      <p className={styles.subtitle}>{lang('CxAuthNameSubtitle')}</p>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.nameFields}>
          <InputText
            id="registration-first-name"
            label={lang('CxAuthFirstName')}
            value={firstName}
            autoComplete="given-name"
            error={errorKey && lang.withRegular(errorKey)}
            autoFocus
            onChange={handleFirstNameChange}
          />
          <InputText
            id="registration-last-name"
            label={lang('CxAuthLastName')}
            value={lastName}
            autoComplete="family-name"
            onChange={handleLastNameChange}
          />
        </div>
        <Button className={styles.submit} type="submit" disabled={!firstName.trim()} isLoading={isLoading}>
          {lang('CxAuthContinue')}
        </Button>
      </form>
    </div>
  );
};

export default memo(withGlobal((global): Complete<StateProps> => {
  const { isLoading, errorKey } = global.auth;

  return {
    isLoading,
    errorKey,
  };
})(AuthName));
